import React, { useState, useRef, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAudioRecording from "./useAudioRecording";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ChatScreen.css";
import { apiService } from "./services/apiService";
import { ERROR_MESSAGES, UI_MESSAGES, AUDIO_CONFIG } from "./constants";

const ChatScreen = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Survey data state
  const [surveyData, setSurveyData] = useState(null);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [surveyError, setSurveyError] = useState(null);

  // Session and API state
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsRemaining, setQuestionsRemaining] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [summary, setSummary] = useState("");
  const [totalQuestionsAsked, setTotalQuestionsAsked] = useState(0);
  const [surveyName, setSurveyName] = useState("");

  // UI state
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isManualInput, setIsManualInput] = useState(false);

  // Fetch survey data on component mount
  useEffect(() => {
    if (!id) {
      // Redirect to error page or show error if no ID
      setSurveyError(ERROR_MESSAGES.INVALID_SURVEY_ID);
      setIsLoadingSurvey(false);
      return;
    }
    fetchSurveyData();
  }, [id]);

  // Initialize chat once survey data is loaded
  useEffect(() => {
    if (surveyData && !sessionId) {
      initializeChat();
    }
  }, [surveyData, sessionId]);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchSurveyData = async () => {
    setIsLoadingSurvey(true);
    setSurveyError(null);
    try {
      const response = await apiService.fetchSurveyData(id);
      setSurveyData(response.payload.survey_data);
      setSurveyName(response.payload.survey_name || "");
    } catch (error) {
      console.error("Error fetching survey data:", error);
      setSurveyError(error.message);
    } finally {
      setIsLoadingSurvey(false);
    }
  };

  const initializeChat = async () => {
    setIsInitializing(true);
    try {
      const response = await apiService.generateQuestions(surveyData);
      const { session_id, summary, q1, total_questions } = response;

      // Set session state
      setSessionId(session_id);
      setCurrentQuestion(q1);
      setTotalQuestions(total_questions);
      setQuestionsRemaining(total_questions);

      // Add the summary and first question to chat
      const summaryMessage = {
        id: 1,
        sender: "bot",
        message: summary,
        timestamp: new Date(),
      };

      const firstQuestionMessage = {
        id: 2,
        sender: "bot",
        message: q1,
        timestamp: new Date(),
      };

      setChatMessages([summaryMessage, firstQuestionMessage]);
    } catch (error) {
      console.error("Error initializing chat:", error);
      const errorMessage = {
        id: 1,
        sender: "bot",
        message: error.message,
        timestamp: new Date(),
      };
      setChatMessages([errorMessage]);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleSendMessage = async () => {
    if ((currentMessage.trim() || audioFile) && !isFinished) {
      const userMessage = currentMessage || AUDIO_CONFIG.VOICE_MESSAGE_TEXT;

      // Add user message to UI
      const newUserMessage = {
        id: chatMessages.length + 1,
        sender: "user",
        message: userMessage,
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, newUserMessage]);
      setCurrentMessage("");
      setIsManualInput(false);
      cancelRecording();

      setIsBotTyping(true);
      try {
        // Call chat API
        const botResponse = await apiService.sendChatMessage(
          sessionId,
          userMessage,
          currentQuestion,
        );
        setIsBotTyping(false);

        if (botResponse.is_finished) {
          // Chat is complete, show summary
          setIsFinished(true);
          setSummary(botResponse.summary);
          setTotalQuestionsAsked(botResponse.total_questions_asked);

          const summaryMessage = {
            id: chatMessages.length + 2,
            sender: "bot",
            message: UI_MESSAGES.SURVEY_COMPLETE,
            timestamp: new Date(),
          };

          const updatedMessages = [
            ...chatMessages,
            newUserMessage,
            summaryMessage,
          ];
          setChatMessages(updatedMessages);

          // Save conversation to database
          try {
            // Format conversation for API
            // Start from index 1 (first question) and pair each bot question with the following user response
            const conversation = [];
            for (let i = 1; i < updatedMessages.length - 1; i += 2) {
              const botMessage = updatedMessages[i];
              const humanMessage = updatedMessages[i + 1];

              if (
                botMessage &&
                humanMessage &&
                botMessage.sender === "bot" &&
                humanMessage.sender === "user"
              ) {
                conversation.push({
                  bot: botMessage.message,
                  human: humanMessage.message,
                });
              }
            }

            await apiService.saveConversation(
              id,
              conversation,
              botResponse.summary,
            );
          } catch (error) {
            console.error("Failed to save conversation:", error);
            // Don't show error to user to avoid disrupting experience
          }
        } else {
          // Continue with next question
          setCurrentQuestion(botResponse.next_question);
          setQuestionsRemaining(botResponse.questions_remaining);

          const nextQuestionMessage = {
            id: chatMessages.length + 2,
            sender: "bot",
            message: botResponse.next_question,
            timestamp: new Date(),
          };

          setChatMessages((prev) => [...prev, nextQuestionMessage]);
        }
      } catch (err) {
        console.error("Error in chat:", err);
        setIsBotTyping(false);

        const errorMessage = {
          id: chatMessages.length + 2,
          sender: "bot",
          message: err.message,
          timestamp: new Date(),
        };

        setChatMessages((prev) => [...prev, errorMessage]);
      }
    }
  };
  const {
    isRecording,
    recordingTime,
    audioFile,
    transcribedText,
    isTranscribing,
    startRecording,
    stopRecording,
    cancelRecording,
    formatTime,
    resetAudio,
  } = useAudioRecording();

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      // Clear all states when starting new recording
      setCurrentMessage("");
      setIsManualInput(false);
      resetAudio(); // Clear any previous audio/transcription states
      startRecording();
    }
  };

  // Update currentMessage when transcription is completed
  useEffect(() => {
    if (transcribedText && !isTranscribing && !isManualInput && audioFile) {
      setCurrentMessage(transcribedText);
    }
  }, [transcribedText, isTranscribing, isManualInput, audioFile]);

  // Refocus input after bot finishes responding
  useEffect(() => {
    if (!isBotTyping && !isFinished && !isTranscribing) {
      inputRef.current?.focus();
    }
  }, [isBotTyping, isFinished, isTranscribing]);

  return (
    <div className="chat-screen-container d-flex justify-content-center align-items-center min-vh-100">
      <div className="chat-container bg-white shadow rounded-4 overflow-hidden position-relative">
        {/* Watermark - Fixed position relative to chat container */}
        <div
          className="chat-watermark"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/assets/main_logo.png)`,
          }}
        ></div>

        {/* Header */}
        <div className="chat-header text-white p-3 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="chat-header-logo rounded-circle bg-white me-3 d-flex align-items-center justify-content-center overflow-hidden">
              <img src="/assets/ai_logo.png" alt="AI Logo" />
            </div>
            <div>
              <h5 className="mb-0 fw-bold">{surveyName}</h5>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="chat-header-close-btn text-white p-0"
              onClick={() => window.location.reload()}
            >
              ×
            </Button>
          </div>
        </div>

        {/* Chat Messages Container */}
        <div className="chat-messages-container chat-messages p-3 overflow-auto position-relative">
          {isLoadingSurvey ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <div className="spinner-border spinner-custom" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-muted">
                  {UI_MESSAGES.LOADING_SURVEY}
                </div>
              </div>
            </div>
          ) : surveyError ? (
            <div className="chat-messages-container chat-messages p-3 position-relative">
              <div
                className="chat-watermark"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/assets/main_logo.png)`,
                }}
              ></div>
              <div className="d-flex justify-content-center align-items-center h-100">
                <div className="text-center">
                  <div className="d-flex align-items-start justify-content-center">
                    <div>
                      <div className="bot-message-bubble bg-danger text-white p-4 rounded-3 shadow-sm">
                        <h6 className="mb-3">⚠️ Error</h6>
                        <p className="mb-0">{surveyError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : isInitializing ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <div
                  className="spinner-border text-primary spinner-custom"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-muted">
                  {UI_MESSAGES.INITIALIZING_CHAT}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Progress indicator */}
              <div className="mb-3 p-2"></div>
              {/* {!isFinished && totalQuestions > 0 && (
                <div className="mb-3 p-2 bg-light rounded text-center">
                  <small className="text-muted">
                    Question {totalQuestions - questionsRemaining + 1} of{" "}
                    {totalQuestions}
                  </small>
                </div>
              )} */}

              {chatMessages.map((msg) => (
                <div key={msg.id} className="mb-3">
                  {msg.sender === "bot" ? (
                    // Bot Message
                    <div className="d-flex align-items-start">
                      <div className="bot-avatar rounded-circle text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden">
                        <img src="/assets/ai_logo.png" alt="AI Logo" />
                      </div>
                      <div>
                        <div className="bot-message-bubble bg-white p-3 rounded-3 shadow-sm">
                          <p className="bot-message-text mb-0 text-dark text-start">
                            {msg.message}
                          </p>
                          {msg.subtext && (
                            <p className="bot-message-subtext mb-0 mt-2 text-muted fst-italic">
                              {msg.subtext}
                            </p>
                          )}
                        </div>
                        <div className="text-start mt-1">
                          <small className="bot-message-timestamp text-muted">
                            {msg.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </small>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // User Message
                    <div className="d-flex justify-content-end">
                      <div>
                        <div className="user-message-bubble text-white p-3 rounded-3 shadow-sm">
                          <p className="user-message-text mb-0">
                            {msg.message}
                          </p>
                        </div>
                        <div className="text-end mt-1">
                          <small className="user-message-timestamp text-muted">
                            {msg.timestamp.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isBotTyping && (
                <div className="d-flex align-items-start mb-3">
                  <div className="bot-avatar rounded-circle text-white me-2 d-flex align-items-center justify-content-center overflow-hidden">
                    <img src="/assets/ai_logo.png" alt="AI Logo" />
                  </div>

                  <div className="bot-message-bubble bg-white p-3 rounded-3 shadow-sm">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Display */}
              {isFinished && summary && (
                <div className="mb-3">
                  <div className="d-flex align-items-start">
                    <div className="bot-avatar rounded-circle text-white me-2 d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden">
                      <img src="/assets/ai_logo.png" alt="AI Logo" />
                    </div>
                    <div className="w-100">
                      <div className="bot-message-bubble bg-success text-white p-3 rounded-3 shadow-sm">
                        <h6 className="mb-2">📋 Survey Summary</h6>
                        <p className="mb-2">{summary}</p>
                        <small className="opacity-75">
                          Total questions answered: {totalQuestionsAsked}
                        </small>
                      </div>
                      <div className="text-center mt-3">
                        <small className="text-muted fst-italic">
                          {UI_MESSAGES.SURVEY_THANK_YOU}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        {!isFinished && !isInitializing && !isLoadingSurvey && !surveyError && (
          <div className="border-top bg-white p-3">
            <div className="d-flex align-items-center">
              <Form.Control
                ref={inputRef}
                as="textarea"
                rows={6}
                placeholder={
                  isTranscribing
                    ? UI_MESSAGES.TRANSCRIBING_AUDIO
                    : UI_MESSAGES.TYPE_MESSAGE_PLACEHOLDER
                }
                value={currentMessage}
                onChange={(e) => {
                  setCurrentMessage(e.target.value);
                  setIsManualInput(true);
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={isTranscribing || isFinished || isBotTyping}
                className="message-input rounded-3 px-3 py-2 me-2"
                style={{
                  resize: "none",
                  height: "80px",
                  overflow: "auto",
                }}
              />
              <Button
                onClick={handleSendMessage}
                disabled={
                  (!currentMessage.trim() && !audioFile) ||
                  isTranscribing ||
                  isFinished ||
                  isBotTyping
                }
                className="send-button rounded-circle d-flex align-items-center justify-content-center"
              >
                <i className="send-button-icon bi bi-send-fill"></i>
              </Button>
              <Button
                onClick={toggleRecording}
                disabled={isTranscribing || isFinished || isBotTyping}
                className={`voice-button rounded-circle d-flex align-items-center justify-content-center ms-2 ${
                  isRecording
                    ? "listening"
                    : isTranscribing
                      ? "transcribing"
                      : "not-listening"
                }`}
                title={
                  isRecording
                    ? `Stop recording - ${formatTime(recordingTime)}`
                    : isTranscribing
                      ? "Transcribing audio..."
                      : "Start voice recording"
                }
                style={{
                  minWidth: "40px",
                  borderRadius: isRecording ? "20px" : "50%",
                  transition: "all 0.3s ease",
                }}
              >
                {isRecording ? (
                  <span className="d-flex align-items-center">
                    <small style={{ fontSize: "10px", fontWeight: "bold" }}>
                      {formatTime(recordingTime)}
                    </small>
                  </span>
                ) : isTranscribing ? (
                  <i className="voice-button-icon bi bi-hourglass-split"></i>
                ) : (
                  <i className="voice-button-icon bi bi-mic-fill"></i>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatScreen;
